import { useState, useEffect } from "react";
import api, { formatApiError, imgUrl } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const RETURN_STATUSES = ["pending", "approved", "rejected", "received", "processed"];
const MOD_STATUSES = ["pending", "approved", "rejected", "resolved"];
const REFUND_STATUSES = ["", "pending", "processed"];
const EXCHANGE_STATUSES = ["", "pending", "shipped", "delivered"];
const STATUS_CLS = {
  pending: "bg-gold-light text-[#111]", approved: "bg-green-100 text-green-900",
  rejected: "bg-red-100 text-red-900", received: "bg-blue-100 text-blue-900",
  processed: "bg-green-100 text-green-900", resolved: "bg-green-100 text-green-900",
};
const MOD_LABELS = { address: "Address correction", quantity: "Quantity change", product: "Product change", other: "Other" };

export default function AdminReturns() {
  const [tab, setTab] = useState("returns");
  const [returns, setReturns] = useState(null);
  const [mods, setMods] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    api.get("/admin/returns").then((r) => setReturns(r.data)).catch(() => {});
    api.get("/admin/modifications").then((r) => setMods(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const saveReturn = async (r, patch) => {
    try {
      await api.put(`/admin/returns/${r.id}`, patch);
      toast.success("Request updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveMod = async (m, patch) => {
    try {
      await api.put(`/admin/modifications/${m.id}`, patch);
      toast.success("Request updated");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!returns || !mods) return <div className="h-40 bg-muted animate-pulse" />;

  return (
    <div data-testid="admin-returns">
      <h1 className="font-serif text-3xl font-semibold mb-2">Returns & Requests</h1>
      <p className="text-xs text-muted-foreground mb-8">Refunds are always processed manually by you — this page tracks status only, it never moves money.</p>

      <div className="flex gap-1 border-b border-black/10 mb-8">
        {[["returns", `Returns / Refunds / Exchanges (${returns.length})`], ["mods", `Order Change Requests (${mods.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-3 text-xs uppercase tracking-widest border-b-2 transition-colors duration-300 ${tab === id ? "border-gold text-gold" : "border-transparent text-muted-foreground"}`}
            data-testid={`returns-tab-${id}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "returns" && (
        returns.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-white border border-black/10 p-12 text-center" data-testid="no-returns">No return/refund/exchange requests yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {returns.map((r) => (
              <ReturnRow key={r.id} r={r} expanded={expanded === r.id} onToggle={() => setExpanded(expanded === r.id ? null : r.id)} onSave={saveReturn} />
            ))}
          </div>
        )
      )}

      {tab === "mods" && (
        mods.length === 0 ? (
          <p className="text-sm text-muted-foreground bg-white border border-black/10 p-12 text-center" data-testid="no-mods">No order change requests yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {mods.map((m) => (
              <ModRow key={m.id} m={m} expanded={expanded === m.id} onToggle={() => setExpanded(expanded === m.id ? null : m.id)} onSave={saveMod} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function ReturnRow({ r, expanded, onToggle, onSave }) {
  const [notes, setNotes] = useState(r.admin_notes || "");
  return (
    <div className="bg-white border border-black/10" data-testid={`return-${r.id}`}>
      <button onClick={onToggle} className="w-full flex flex-wrap items-center gap-3 p-4 text-left" data-testid={`return-toggle-${r.id}`}>
        <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-gold-light text-[#111]">{r.type}</span>
        <span className="font-medium text-sm">{r.order_number}</span>
        <span className="text-xs text-muted-foreground flex-1">{r.customer?.name}</span>
        <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString("en-IN")}</span>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_CLS[r.status] || "bg-muted"}`} data-testid={`return-status-${r.id}`}>{r.status}</span>
      </button>
      {expanded && (
        <div className="border-t border-black/10 p-5 text-sm" data-testid={`return-details-${r.id}`}>
          <p><span className="text-muted-foreground">Items:</span> {r.items?.map((i) => `${i.name} × ${i.qty}`).join(", ")}</p>
          <p className="mt-1"><span className="text-muted-foreground">Reason:</span> {r.reason}</p>
          {r.description && <p className="mt-1 bg-muted/50 p-3">{r.description}</p>}
          {r.image && <a href={imgUrl(r.image)} target="_blank" rel="noreferrer"><img src={imgUrl(r.image)} alt="Customer evidence" className="w-24 h-24 object-cover border border-black/10 mt-3" data-testid={`return-evidence-${r.id}`} /></a>}
          <p className="mt-2 text-xs text-muted-foreground">{r.customer?.email} · {r.customer?.phone}</p>
          <div className="grid md:grid-cols-4 gap-3 mt-4 items-end">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Status</p>
              <Select value={r.status} onValueChange={(v) => onSave(r, { status: v })}>
                <SelectTrigger className="rounded-sm bg-white h-9 text-xs" data-testid={`return-status-select-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{RETURN_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Refund Status</p>
              <Select value={r.refund_status || "none"} onValueChange={(v) => onSave(r, { refund_status: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm bg-white h-9 text-xs" data-testid={`return-refund-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{REFUND_STATUSES.map((s) => <SelectItem key={s || "none"} value={s || "none"}>{s || "none"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Exchange Status</p>
              <Select value={r.exchange_status || "none"} onValueChange={(v) => onSave(r, { exchange_status: v === "none" ? "" : v })}>
                <SelectTrigger className="rounded-sm bg-white h-9 text-xs" data-testid={`return-exchange-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{EXCHANGE_STATUSES.map((s) => <SelectItem key={s || "none"} value={s || "none"}>{s || "none"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Input placeholder="Internal note" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-white h-9 text-xs" data-testid={`return-notes-${r.id}`} />
              <button onClick={() => onSave(r, { admin_notes: notes })} className="border border-black/20 px-4 h-9 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300" data-testid={`return-notes-save-${r.id}`}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ModRow({ m, expanded, onToggle, onSave }) {
  const [notes, setNotes] = useState(m.admin_notes || "");
  return (
    <div className="bg-white border border-black/10" data-testid={`mod-${m.id}`}>
      <button onClick={onToggle} className="w-full flex flex-wrap items-center gap-3 p-4 text-left" data-testid={`mod-toggle-${m.id}`}>
        <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-gold-light text-[#111]">{MOD_LABELS[m.type] || m.type}</span>
        <span className="font-medium text-sm">{m.order_number}</span>
        <span className="text-xs text-muted-foreground flex-1">{m.customer?.name}</span>
        <span className="text-xs text-muted-foreground">{new Date(m.created_at).toLocaleDateString("en-IN")}</span>
        <span className={`text-[10px] uppercase tracking-widest px-2 py-1 ${STATUS_CLS[m.status] || "bg-muted"}`} data-testid={`mod-status-${m.id}`}>{m.status}</span>
      </button>
      {expanded && (
        <div className="border-t border-black/10 p-5 text-sm" data-testid={`mod-details-${m.id}`}>
          <p className="bg-muted/50 p-3">{m.message}</p>
          {m.address && (
            <p className="mt-2"><span className="text-muted-foreground">Corrected address:</span> {m.address.line1}{m.address.line2 ? `, ${m.address.line2}` : ""}, {m.address.city}, {m.address.state} — {m.address.pincode}</p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">{m.customer?.email} · {m.customer?.phone}</p>
          <div className="flex gap-3 mt-4 items-end">
            <Select value={m.status} onValueChange={(v) => onSave(m, { status: v })}>
              <SelectTrigger className="rounded-sm bg-white h-9 text-xs w-40" data-testid={`mod-status-select-${m.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>{MOD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Internal note" value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-white h-9 text-xs flex-1" data-testid={`mod-notes-${m.id}`} />
            <button onClick={() => onSave(m, { admin_notes: notes })} className="border border-black/20 px-4 h-9 text-xs uppercase tracking-widest hover:border-gold hover:text-gold transition-colors duration-300" data-testid={`mod-notes-save-${m.id}`}>Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
